import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type AnnouncementResponseOptionModel from 'garaje/models/announcement-response-option';
import type AnnouncementTemplateCategoryModel from 'garaje/models/announcement-template-category';
import type CompanyModel from 'garaje/models/company';
import type EmployeeModel from 'garaje/models/employee';
import type EmployeeGroupModel from 'garaje/models/employee-group';
import type LocationModel from 'garaje/models/location';
import type UserModel from 'garaje/models/user';

import type AnnouncementAttachmentModel from './announcement-attachment';

export type AnnouncementResponseCategory = 'safe' | 'need_help' | 'other';

export type AnnouncementResponseOption = {
  label: string;
  category: AnnouncementResponseCategory;
};

export default class AnnouncementTemplateModel extends Model {
  // Relationships
  @belongsTo('company', { async: true, inverse: null }) declare company: AsyncBelongsTo<CompanyModel>;
  @belongsTo('user', { async: true, inverse: null }) declare creator: AsyncBelongsTo<UserModel>;
  @belongsTo('announcement-template-category', { async: true, inverse: 'announcementTemplates' })
  declare announcementTemplateCategory: AsyncBelongsTo<AnnouncementTemplateCategoryModel>;
  @hasMany('location', { async: true, inverse: null }) declare locations: AsyncHasMany<LocationModel>;
  @hasMany('employee-group', { async: true, inverse: null }) declare employeeGroups: AsyncHasMany<EmployeeGroupModel>;
  @hasMany('employee', { async: true, inverse: null }) declare employees: AsyncHasMany<EmployeeModel>;
  @hasMany('announcement-response-option', { async: true, inverse: 'announcementTemplate' })
  declare announcementResponseOptions: AsyncHasMany<AnnouncementResponseOptionModel>;
  @hasMany('announcement-attachment', { async: true, inverse: 'announcementTemplate' })
  declare announcementAttachments: AsyncHasMany<AnnouncementAttachmentModel>;

  // Attributes
  @attr('string') declare title: string;
  @attr('string') declare name: string;
  @attr('string') declare message: string;
  @attr('array') declare defaultChannels: string[];
  @attr('array') declare defaultEmployeeAudiences: string[];
  @attr('array') declare defaultVisitorAudiences: string[];
  @attr('array') declare defaultEmployeeGroups: string[];
  @attr('array') declare defaultEmployees: string[];
  @attr('array') declare defaultAnnouncementResponseOptions: AnnouncementResponseOption[];
  @attr('array', { defaultValue: () => [] }) declare locationNames: string[];
  @attr('boolean') declare published: boolean;
  @attr('boolean') declare markAsSafe: boolean;
  @attr('boolean') declare rollCallEnabled: boolean;
  @attr('boolean') declare takeoverActive: boolean;
  @attr('boolean') declare twoWayChatEnabled: boolean;
  @attr('date') declare deletedAt: Date;
  @attr('date') declare createdAt: Date;
  @attr('date') declare updatedAt: Date;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'announcement-template': AnnouncementTemplateModel;
  }
}
